'use client';

import { useState, useCallback, useRef, useEffect } from 'react';

const STANDARD_RATIOS = [1, 3 / 4, 4 / 3, 9 / 16, 16 / 9, 3 / 2, 2 / 3];

function closestRatio(w: number, h: number): number {
	const actual = w / h;
	return STANDARD_RATIOS.reduce((prev, curr) => (Math.abs(curr - actual) < Math.abs(prev - actual) ? curr : prev));
}

/**
 * SmartCoverImage — cover image with explicit pixel height.
 * Uses ResizeObserver to compute height from actual container width,
 * avoiding the aspect-ratio timing bug with react-masonry-css.
 */
export function SmartCoverImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
	const [loaded, setLoaded] = useState(false);
	const [ratio, setRatio] = useState(4 / 3);
	const containerRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(150);

	const syncHeight = useCallback((r: number) => {
		const w = containerRef.current?.clientWidth;
		if (w && w > 0) setHeight(Math.round(w / r));
	}, []);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => syncHeight(ratio));
		ro.observe(el);
		return () => ro.disconnect();
	}, [ratio, syncHeight]);

	const handleLoad = useCallback(
		(e: React.SyntheticEvent<HTMLImageElement>) => {
			const { naturalWidth, naturalHeight } = e.currentTarget;
			if (naturalWidth && naturalHeight) {
				const r = closestRatio(naturalWidth, naturalHeight);
				setRatio(r);
				syncHeight(r);
			}
			setLoaded(true);
		},
		[syncHeight],
	);

	return (
		<div className={`relative w-full overflow-hidden bg-muted/30 ${className ?? ''}`} ref={containerRef} style={{ height }}>
			{!loaded && <div className="absolute inset-0 animate-pulse bg-muted/60" />}
			{/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: onLoad is not interactive */}
			<img
				alt={alt}
				className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
				loading="lazy"
				onLoad={handleLoad}
				src={src}
			/>
		</div>
	);
}
